import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Users, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CreateUserFormData } from '@/schema/user';

interface UserBasicInfoProps {
  form: UseFormReturn<CreateUserFormData>;
}

export function UserBasicInfo({ form }: UserBasicInfoProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Watch form values for password validation display
  const password = form.watch('password');
  const confirmPassword = form.watch('confirmPassword');

  // Password validation utility function
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8)
      errors.push(t('users.create.validation.passwordMinLength'));
    if (!/\d/.test(password))
      errors.push(t('users.create.validation.passwordNumber'));
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password))
      errors.push(t('users.create.validation.passwordSpecial'));
    return errors;
  };

  const passwordErrors = password ? validatePassword(password) : [];

  return (
    <Card>
      <CardHeader dir={isRTL ? 'ltr' : 'rtl'}>
        <CardTitle
          className={cn(
            'flex items-center gap-2',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <Users className="w-5 h-5" />
          {t('users.create.basicInfo.title')}
        </CardTitle>
        <CardDescription className={isRTL ? 'text-right' : 'text-left'}>
          {t('users.create.basicInfo.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={isRTL ? 'text-right' : 'text-left'}>
                  {t('users.create.fields.fullName')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className={cn(isRTL ? 'text-right' : 'text-left')}
                    placeholder={t('users.create.placeholders.enterFullName')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </FormControl>
                <FormMessage className={isRTL ? 'text-right' : 'text-left'} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={isRTL ? 'text-right' : 'text-left'}>
                  {t('users.create.fields.emailAddress')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    className={cn(isRTL ? 'text-right' : 'text-left')}
                    placeholder={t('users.create.placeholders.enterEmail')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </FormControl>
                <FormMessage className={isRTL ? 'text-right' : 'text-left'} />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={isRTL ? 'text-right' : 'text-left'}>
                  {t('users.create.fields.password')}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      className={cn(isRTL ? 'text-right pr-10' : 'text-left pr-10')}
                      placeholder={t('users.create.placeholders.enterPassword')}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage className={isRTL ? 'text-right' : 'text-left'} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={isRTL ? 'text-right' : 'text-left'}>
                  {t('users.create.fields.confirmPassword')}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={cn(isRTL ? 'text-right pr-10' : 'text-left pr-10')}
                      placeholder={t('users.create.placeholders.confirmPassword')}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage className={isRTL ? 'text-right' : 'text-left'} />
              </FormItem>
            )}
          />
        </div>

        {/* Password Requirements */}
        <div className="p-4 bg-muted/30 rounded-lg">
          <p
            className={cn(
              'text-sm font-medium mb-2',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            {t('users.create.passwordRequirements.title')}
          </p>
          <ul
            className={cn(
              'text-sm space-y-1',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            <li
              className={
                passwordErrors.includes(
                  t('users.create.validation.passwordMinLength')
                )
                  ? 'text-destructive'
                  : 'text-muted-foreground'
              }
            >
              {isRTL ? '•' : '•'}{' '}
              {t('users.create.passwordRequirements.minLength')}
            </li>
            <li
              className={
                passwordErrors.includes(
                  t('users.create.validation.passwordNumber')
                )
                  ? 'text-destructive'
                  : 'text-muted-foreground'
              }
            >
              {isRTL ? '•' : '•'}{' '}
              {t('users.create.passwordRequirements.oneNumber')}
            </li>
            <li
              className={
                passwordErrors.includes(
                  t('users.create.validation.passwordSpecial')
                )
                  ? 'text-destructive'
                  : 'text-muted-foreground'
              }
            >
              {isRTL ? '•' : '•'}{' '}
              {t('users.create.passwordRequirements.oneSpecial')}
            </li>
            <li
              className={
                password !== confirmPassword && confirmPassword
                  ? 'text-destructive'
                  : 'text-muted-foreground'
              }
            >
              {isRTL ? '•' : '•'}{' '}
              {t('users.create.passwordRequirements.mustMatch')}
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
